import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiClock, FiCheckCircle, FiArrowRight } = FiIcons;

const HeroSection = () => {
  return (
    <section className="relative bg-gradient-to-br from-green-50 via-blue-50 to-primary-50 pt-24 pb-16 overflow-hidden min-h-[90vh] flex items-center">
      {/* Background pattern or overlay */}
      <div className="absolute inset-0 bg-white/80"></div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="text-left"
          >
            {/* Partnership Logos */}
            <div className="flex items-center justify-start mb-8">
              <div className="flex items-center space-x-4">
                <img
                  src="https://app1.sharemyimage.com/2025/05/19/burbank-insurance-services-in-california-logo.png"
                  alt="Burbank Insurance Services"
                  className="h-12 w-12"
                />
                <span className="text-2xl font-bold text-secondary-700">×</span>
                <img
                  src="https://app1.sharemyimage.com/2025/08/18/ethos-logo_RGB_cypress.jpg"
                  alt="Ethos"
                  className="h-12 w-auto"
                  onError={(e) => {
                    // Fallback to text if image fails to load
                    e.target.outerHTML = '<div class="text-4xl font-bold text-primary-700 tracking-wide">ETHOS</div>';
                  }}
                />
              </div>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-secondary-900 mb-6 leading-tight">
              Get instant life insurance
            </h1>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-medium text-secondary-600 mb-8 leading-tight">
              Protect your loved ones
            </h2>

            {/* Subtitle */}
            <p className="text-lg text-secondary-600 mb-8 max-w-lg">
              No medical exams or blood tests.<sup className="text-sm">1</sup>
            </p>

            {/* CTA Button */}
            <div className="mb-12">
              <a
                href="https://quote.burbankinsuranceservices.com/Dt2tCn"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-8 py-4 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors shadow-lg text-lg"
              >
                Get my rates
                <SafeIcon icon={FiArrowRight} className="h-5 w-5 ml-2" />
              </a>
            </div>

            {/* Media Trust Indicators - Static 4 Logos */}
            <div className="flex items-center justify-center lg:justify-start space-x-8">
              <img
                src="https://app1.sharemyimage.com/2025/08/18/cdnlogo.com_business-insider.png"
                alt="Business Insider"
                className="h-8 w-auto opacity-60 hover:opacity-100 transition-opacity"
              />
              <img
                src="https://app1.sharemyimage.com/2025/08/18/png-clipart-logo-forbes-industry-magazine-black-cover-company-text.png"
                alt="Forbes"
                className="h-8 w-auto opacity-60 hover:opacity-100 transition-opacity"
              />
              <img
                src="https://app1.sharemyimage.com/2025/08/18/FORTUNE-LOGO-2016.png"
                alt="Fortune"
                className="h-8 w-auto opacity-60 hover:opacity-100 transition-opacity"
              />
              <img
                src="https://app1.sharemyimage.com/2025/08/18/603-6035540_yahoo-finance-logo-png-transparent-png.png"
                alt="Yahoo Finance"
                className="h-8 w-auto opacity-60 hover:opacity-100 transition-opacity"
              />
            </div>
          </motion.div>

          {/* Right Content - Image */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative flex justify-center lg:justify-end"
          >
            <div className="relative rounded-lg overflow-hidden">
              <img
                src="https://app1.sharemyimage.com/2025/05/23/Son_And_Dad_Playing_Together_original_399536.jpg"
                alt="Happy family - father playing with children"
                className="w-full max-w-lg h-auto object-cover rounded-lg shadow-2xl"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          </motion.div>
        </div>

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 bg-white rounded-xl shadow-lg p-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-primary-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <SafeIcon icon={FiClock} className="h-8 w-8 text-primary-600" />
              </div>
              <h3 className="font-semibold text-secondary-900 mb-2">5-Minute Application</h3>
              <p className="text-sm text-secondary-600">Quick online application process with instant decisions for qualifying applicants.</p>
            </div>
            
            <div className="text-center">
              <div className="bg-green-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <SafeIcon icon={FiCheckCircle} className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-secondary-900 mb-2">No Medical Exam</h3>
              <p className="text-sm text-secondary-600">Skip the medical exam and blood tests. Just answer a few health questions online.</p>
            </div>
            
            <div className="text-center">
              <div className="bg-blue-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <SafeIcon icon={FiShield} className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-secondary-900 mb-2">Up to $3M Coverage</h3>
              <p className="text-sm text-secondary-600">Substantial coverage options to protect your family's financial future and lifestyle.</p>
            </div>
          </div>
        </motion.div>

        {/* Licensed Agent Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-8 text-center"
        >
          <div className="inline-flex items-center bg-secondary-100 rounded-full px-6 py-3">
            <SafeIcon icon={FiShield} className="h-5 w-5 text-primary-600 mr-2" />
            <span className="text-sm font-medium text-secondary-700">
              Licensed in California - CA License #6013543 | Powered by Ethos
            </span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;